<script setup lang="ts">
import Button from '~/components/design-system/button.vue';
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import { useServices } from '~/composables/useServices';
import UserCard from '~/components/user-interface/user-card.vue';
import { ESumSubAction } from '~/interfaces/dto/compliance/request';
import type { IRegisterResponse } from '~/interfaces/dto/auth/response';
import GenericCountdownBar from '~/components/user-interface/generic-countdown-bar.vue';

const { $t } = useNuxtApp();
const auth = useAuthStore();
const site = useSiteStore();

const sumsubActive = ref<boolean>(false);

withDefaults(
  defineProps<{ registrationResponse: Partial<IRegisterResponse> }>(),
  {},
);

const sumSubVerificationType = computed<ESumSubAction>(() =>
  auth.currentUser?.idNumberType?.toLocaleLowerCase() === 'idnumber'
    ? ESumSubAction.DocumentVerify
    : ESumSubAction.PassportVerify,
);
const { $marketingService } = useServices();

const requiresSumsub = computed(() => auth.getSumsubRequired);
const requiresDocVerification = computed(() => auth.getCbpRequired);

onMounted(() => {
  if (auth.getWelcomeOffer && auth.loggedIn) {
    $marketingService.setMarketingOptIn({
      vendorAccountID: auth.user_id,
      optinName: 'jpcwelcomeoffer',
      source: 'Web',
      countryCode: site.getRegionCode,
    });
  }
});

function completeReg() {
  site.activateModal(
    auth.getPartialPostReg
      ? 'registrationPartialCompletion'
      : 'registrationFullCompletion',
  );
}

function depositNow() {
  site.deactivateModal();
  auth.toggleAccountModal(true);
  auth.setAccountModalPage('deposit');
}

function depositLater() {
  site.deactivateModal();
}
</script>

<template>
  <UserSumsub
    v-model="sumsubActive"
    :action="sumSubVerificationType"
    :pregenerated-token="
      registrationResponse?.data?.complianceResponse?.accessToken
    "
    @complete="site.deactivateModal()"
  >
    <div>
      <div class="px-2">
        <UserCard class="mx-auto" />
      </div>
      <div class="bg-base p-2 text-base-priority">
        <div v-if="requiresSumsub" class="space-y-2">
          <GenericCountdownBar
            :duration="10000"
            @finished="sumsubActive = true"
          />
          <div class="font-bold text-sm">
            {{ $t('start-verification-process') }}
          </div>
          <p class="text-sm" v-html="$t('verification-process-explaination')" />
          <Button class="w-full justify-center" @click="sumsubActive = true">
            {{ $t('verify-now') }}
          </Button>
        </div>
        <div v-else class="space-y-2">
          <div class="font-bold text-center">{{ $t('welcome-to-jpc') }}</div>
          <div class="text-sm text-center" v-html="$t('account-shown-above')" />
          <div class="grid grid-cols-2 gap-2">
            <Button
              type="other"
              class="w-full justify-center"
              @click="depositNow()"
            >
              {{ $t('deposit-now') }}
            </Button>
            <Button
              class="w-full justify-center"
              type="secondary-alternative"
              @click="requiresDocVerification ? completeReg() : depositLater()"
            >
              {{
                requiresDocVerification
                  ? $t('complete-your-account')
                  : $t('deposit-later')
              }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </UserSumsub>
</template>
